import React, { useCallback, useState } from 'react';
import { Modal, Radio, Typography, message } from 'antd-v5';
import FileDownload from 'js-file-download';
import { exportRolexInquiryDetail, exportRolexInquirySummary } from '../../../../api/vendors';

const { Text } = Typography;

const OPTIONS = [
  {
    value: 'summary',
    label: 'Summary report',
    description: 'A ZIP file containing one CSV per store with waiting counts by model.',
  },
  {
    value: 'detail',
    label: 'Detailed report',
    description: 'A single CSV of every inquiry with client, associate, story, and model details.',
  },
];

const formatRangeLabel = dateRange => {
  if (!dateRange?.[0] || !dateRange?.[1]) return 'All time';
  return `${dateRange[0].format('MMM D, YYYY')} – ${dateRange[1].format('MMM D, YYYY')}`;
};

const buildDateRangeQuery = dateRange => {
  if (!dateRange?.[0] || !dateRange?.[1]) return {};
  return {
    startDate: dateRange[0].format('YYYYMMDD'),
    endDate: dateRange[1].format('YYYYMMDD'),
  };
};

const ExportInquiriesModal = ({ open, onClose, dateRange }) => {
  const [reportType, setReportType] = useState('summary');
  const [loading, setLoading] = useState(false);

  const handleCancel = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      const query = buildDateRangeQuery(dateRange);
      const fetcher = reportType === 'summary' ? exportRolexInquirySummary : exportRolexInquiryDetail;
      const { blob, filename } = await fetcher(query);
      FileDownload(blob, filename);
      message.success('Export downloaded');
      onClose();
    } catch (error) {
      console.error('Rolex inquiry export failed', error);
      message.error('There was an error generating your export. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, onClose, reportType]);

  return (
    <Modal
      title="Export Rolex inquiries"
      open={open}
      onOk={handleExport}
      onCancel={handleCancel}
      okText="Export"
      confirmLoading={loading}
      cancelButtonProps={{ disabled: loading }}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose
      width={480}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Text type="secondary">
          Date range: <Text strong>{formatRangeLabel(dateRange)}</Text>
        </Text>
        <Radio.Group
          value={reportType}
          onChange={e => setReportType(e.target.value)}
          disabled={loading}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {OPTIONS.map(option => (
            <Radio key={option.value} value={option.value} style={{ alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text strong>{option.label}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {option.description}
                </Text>
              </div>
            </Radio>
          ))}
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default ExportInquiriesModal;
